<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openCreateModal"
    >
      Nova ação massiva
    </woot-button>

    <div class="row">
      <template v-if="isEmptyState">
        <div class="small-8 columns">
          <div class="no-items-error-message bulk-actions-empty-state">
            <img
              class="bulk-actions-empty-state__image"
              src="/assets/images/dashboard/bulk_actions/no_items.svg"
              alt="Ação massiva"
              title="Ação massiva"
            />
            <p>Parece que não há ações massivas criadas para esta conta ainda</p>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="small-8 columns with-right-space">
          <woot-loading-state
            v-if="uiFlags.isFetching"
            message="Carregando ações massivas"
          />

          <table
            v-if="!uiFlags.isFetching && records.length"
            aria-describedby="bulk-actions-table"
            class="woot-table"
          >
            <thead>
              <th>Nome</th>
              <th>Status</th>
              <th>Agendado para</th>
              <th>Processadas</th>
              <th>Falhas</th>
              <th>Ações</th>
            </thead>
            <tbody>
              <tr v-for="bulkAction in records" :key="bulkAction.id">
                <td>{{ bulkAction.name }}</td>
                <td>{{ humanStatus(bulkAction.status) }}</td>
                <td>{{ readableTime(bulkAction.scheduled_at) }}</td>
                <td>
                  {{ bulkAction.processed_count || 0 }}/{{ bulkAction.total_count || 0 }}
                </td>
                <td>{{ bulkAction.failed_count || 0 }}</td>
                <td class="button-wrapper">
                  <woot-button
                    v-tooltip.top="'Editar'"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    class-names="grey-btn"
                    icon="edit"
                    @click="openEditModal(bulkAction)"
                  />
                  <woot-button
                    v-tooltip.top="'Excluir'"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    @click="openDeletePopup(bulkAction)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>    
      <div class="small-4 columns">
        <h5>Ações massivas</h5>
        <p>As ações massivas permitem aplicar mudanças em grande escala em várias conversas de uma só vez, de acordo com os filtros que você selecionar.</p>
        <p>🔧 Exemplos práticos:</p>
        <ul>
          <li>Reorganização de leads: mover todas as conversas do time comercial marcadas como lead frio para o time de busca e resgate.</li>
          <li>Resolução automática: encerrar todas as conversas que estão adiadas há mais de 45 dias.</li>
          <li>Gestão de equipe: trocar o usuário atribuído em centenas de conversas simultaneamente.</li>
        </ul>
        <p>⏰ Observação importante:</p>
        <p><strong>As ações massivas são sempre executadas fora do horário comercial.</strong><br>
        Mesmo que você cadastre agora, os resultados só estarão disponíveis no dia seguinte.</p>
      </div>
    </div>

    <woot-modal :show.sync="showCreateModal" size="medium" :on-close="closeCreateModal">
      <bulk-action-wizard
        v-if="showCreateModal"
        :on-close="closeCreateModal"
        :bulk-action-to-edit="selectedBulkAction"
        @created="onBulkActionCreated"
        @updated="onBulkActionUpdated"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      title="Excluir ação massiva"
      message="Tem certeza que deseja excluir"
      :message-value="deleteMessage"
      confirm-text="Sim, excluir"
      reject-text="Cancelar"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { format } from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import BulkActionWizard from './BulkActionWizard.vue';

export default {
  components: {
    BulkActionWizard,
  },
  mixins: [alertMixin],
  data() {
    return {
      showCreateModal: false,
      selectedBulkAction: null,
      selectedBulkActionForDelete: null,
      showDeleteConfirmationPopup: false,
    };
  },
  computed: {
    ...mapGetters({
      records: 'bulkActionsSettings/getBulkActions',
      uiFlags: 'bulkActionsSettings/getUIFlags',
    }),
    isEmptyState() {
      return !this.uiFlags.isFetching && !this.records.length;
    },
    deleteMessage() {
      return ` ${this.selectedBulkActionForDelete?.name || ''}?`;
    },
  },
  mounted() {
    this.$store.dispatch('bulkActionsSettings/get');
  },
  methods: {
    openCreateModal() {
      this.selectedBulkAction = null;
      this.showCreateModal = true;
    },
    openEditModal(bulkAction) {
      this.selectedBulkAction = bulkAction;
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.selectedBulkAction = null;
    },
    async onBulkActionCreated() {
      await this.$store.dispatch('bulkActionsSettings/get');
      this.showAlert('Lista de ações massivas atualizada.');
    },
    async onBulkActionUpdated() {
      await this.$store.dispatch('bulkActionsSettings/get');
      this.showAlert('Ação massiva atualizada na lista.');
    },
    openDeletePopup(bulkAction) {
      this.selectedBulkActionForDelete = bulkAction;
      this.showDeleteConfirmationPopup = true;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
      this.selectedBulkActionForDelete = null;
    },
    async confirmDeletion() {
      if (!this.selectedBulkActionForDelete?.id) {
        return;
      }

      const deleted = await this.$store.dispatch(
        'bulkActionsSettings/delete',
        this.selectedBulkActionForDelete.id
      );

      if (deleted) {
        this.showAlert('Ação massiva excluída com sucesso.');
      } else {
        this.showAlert('Não foi possível excluir a ação massiva.');
      }

      this.closeDeletePopup();
    },
    humanStatus(status) {
      const statusMap = {
        draft: 'Rascunho',
        scheduled: 'Agendada',
        running: 'Executando',
        completed: 'Concluída',
        failed: 'Falhou',
      };

      return statusMap[status] || status;
    },
    readableTime(date) {
      if (!date) {
        return '';
      }
      return format(new Date(date), 'dd/MM/yy - HH:mm');
    },
  },
};
</script>

<style scoped lang="scss">
.bulk-actions-empty-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
}

.bulk-actions-empty-state__image {
  width: 45vh;
  height: 32vh;
  max-width: 100% !important;
}
</style>
